/**
 * Settings (the owner, 2026-10-19: "Make a settings page and add all of these
 * to it"; ADR 0172): every owner setting that had no screen. Owners only: the
 * route is guarded, and the API refuses anyone else (section 6.2). Each
 * person's time zone is theirs, on My settings (ADR 0200), and daily capacity
 * is 8 hours for everyone (ADR 0201): the table of both that was here went in
 * v0.10.0.
 *
 * - **Blocks and counting:** the merge gap, short calls, hold time, the
 *   minutes a Datto RMM job and a ScreenConnect action count, the longest an
 *   open connection stays open, and when a block is already logged.
 * - **Workdays** (WorkdaysSettings.tsx; from Mappings → Techs, ADR 0198):
 *   utilization's available hours are 8 on each.
 * - **Data kept:** how long raw vendor data stays.
 * - **Other settings:** where the rest are, on Mappings.
 *
 * `email_received_minutes` isn't here: nothing uses it since only the email a
 * tech sends counts (ADR 0129), and it's retired (ADR 0172).
 *
 * Each change saves as it's made, as the Mappings settings do (ADR 0082).
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { retryUnlessRefused } from "../../api/client";
import type { AppSettings } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Panel } from "../../components/ui/Panel";
import { type WholeNumberRule, wholeNumberProblem } from "../../lib/settingNumbers";
import { CheckboxField, legendClass, linkClass, Note, NumberField, Warning } from "./fields";
import { useSettingsSection } from "./useSettingsSection";
import { WorkdaysSettings } from "./WorkdaysSettings";

/** The API's limits (backend/app/schemas/settings.py). */
const A_DAY_OF_MINUTES: WholeNumberRule = { unit: "minutes", min: 0, max: 1440, maxNote: " (a day)" };

function rule(value: WholeNumberRule): (text: string) => string | null {
  return (text) => wholeNumberProblem(text, value);
}

function RebuildADay() {
  return (
    <>
      <Link to="/tools" className={linkClass}>
        Owner tools
      </Link>{" "}
      → Days → Rebuild a day
    </>
  );
}

type CountingKey =
  | "merge_gap_minutes"
  | "min_call_seconds"
  | "count_call_hold_time"
  | "rmm_point_action_minutes"
  | "screenconnect_action_minutes"
  | "screenconnect_max_open_hours"
  | "already_logged_overlap_pct";

function BlocksAndCounting({ settings }: { settings: AppSettings }) {
  const { shown, change, statusOf, errors } = useSettingsSection<CountingKey>(settings);
  return (
    <div className="flex flex-col gap-5">
      <fieldset className="rounded-card border border-line bg-panel-2 p-4">
        <legend className={legendClass}>Blocks</legend>
        <div className="grid gap-4 md:grid-cols-2">
          <NumberField
            label="Merge gap"
            unit="minutes"
            hint="Related activities for the same tech and client (sharing a number, contact or ScreenConnect session) join one block when they're at most this many minutes apart; overlapping ones always do. 15 to start; at most 1,440 (a day)."
            min={0}
            max={1440}
            shown={shown("merge_gap_minutes")}
            status={statusOf("merge_gap_minutes")}
            error={errors.merge_gap_minutes}
            problem={rule(A_DAY_OF_MINUTES)}
            onSave={(minutes) => {
              change({ merge_gap_minutes: minutes });
            }}
          />
          <NumberField
            label="Already logged at"
            unit="%"
            hint="A block is dismissed as already logged when the tech's own Autotask time entries for that client cover at least this share of its counted time. 50 to start; 0 to 100."
            min={0}
            max={100}
            shown={shown("already_logged_overlap_pct")}
            status={statusOf("already_logged_overlap_pct")}
            error={errors.already_logged_overlap_pct}
            problem={rule({ unit: "percent", min: 0, max: 100 })}
            onSave={(percent) => {
              change({ already_logged_overlap_pct: percent });
            }}
          />
        </div>
      </fieldset>
      <fieldset className="rounded-card border border-line bg-panel-2 p-4">
        <legend className={legendClass}>What counts</legend>
        <div className="grid gap-4 md:grid-cols-2">
          <NumberField
            label="Shortest call"
            unit="seconds"
            hint="A call with less talk time than this is excluded as short: kept, but never on a block. 20 to start."
            min={0}
            shown={shown("min_call_seconds")}
            status={statusOf("min_call_seconds")}
            error={errors.min_call_seconds}
            problem={rule({ unit: "seconds", min: 0 })}
            onSave={(seconds) => {
              change({ min_call_seconds: seconds });
            }}
          />
          <CheckboxField
            label="Count a call's hold time"
            hint="A call's hold time counts with its talk time. On to start."
            checked={shown("count_call_hold_time")}
            status={statusOf("count_call_hold_time")}
            error={errors.count_call_hold_time}
            onSave={(on) => {
              change({ count_call_hold_time: on });
            }}
          />
          <NumberField
            label="Datto RMM job"
            unit="minutes"
            hint="Each job a tech runs counts this many minutes, ending when it ran. 5 to start; at most 1,440 (a day)."
            min={0}
            max={1440}
            shown={shown("rmm_point_action_minutes")}
            status={statusOf("rmm_point_action_minutes")}
            error={errors.rmm_point_action_minutes}
            problem={rule(A_DAY_OF_MINUTES)}
            onSave={(minutes) => {
              change({ rmm_point_action_minutes: minutes });
            }}
          />
          <NumberField
            label="ScreenConnect action with nobody connected"
            unit="minutes"
            hint="Each such action of a tech's counts this many minutes before it. 5 to start; at most 1,440 (a day)."
            min={0}
            max={1440}
            shown={shown("screenconnect_action_minutes")}
            status={statusOf("screenconnect_action_minutes")}
            error={errors.screenconnect_action_minutes}
            problem={rule(A_DAY_OF_MINUTES)}
            onSave={(minutes) => {
              change({ screenconnect_action_minutes: minutes });
            }}
          />
          <NumberField
            label="Longest open connection"
            unit="hours"
            hint="A ScreenConnect connection, Datto RMM web remote session or SonicWall connection with no end is closed after this many hours, and flagged for the tech to check. 4 to start; 1 to 24."
            min={1}
            max={24}
            shown={shown("screenconnect_max_open_hours")}
            status={statusOf("screenconnect_max_open_hours")}
            error={errors.screenconnect_max_open_hours}
            problem={rule({ unit: "hours", min: 1, max: 24 })}
            onSave={(hours) => {
              change({ screenconnect_max_open_hours: hours });
            }}
          />
        </div>
      </fieldset>
      <Note>
        <p>
          <span className="font-medium text-text">When a change applies.</span> The merge gap and the already-logged
          share apply when a day&apos;s blocks are built: today&apos;s at once, as work arrives, and a day already built
          when it&apos;s built again. The nightly rebuild redoes the last 7 days; for an older day, use{" "}
          <RebuildADay />. The others apply to calls, jobs, actions and connections as they arrive from now on: a
          rebuild doesn&apos;t count again those already in. The SonicWall&apos;s connections are the exception: a
          rebuild makes a day&apos;s network activities again from the lines still kept, with the longest open
          connection as it&apos;s set then.
        </p>
      </Note>
    </div>
  );
}

function DataKept({ settings }: { settings: AppSettings }) {
  const { shown, change, statusOf, errors } = useSettingsSection<"raw_payload_retention_days">(settings);
  return (
    <div className="flex flex-col gap-4">
      <Warning>
        <p>
          <span className="font-medium">This is how long raw vendor data is kept:</span> the records SkySwitch,
          ScreenConnect and Microsoft 365 send, and the SonicWall&apos;s connection lines. Each night, a
          record older than this goes once the work made from it is logged or dismissed; the SonicWall&apos;s lines go
          whatever became of their work. Nothing goes before it&apos;s 8 days old, whatever this says.
        </p>
        <p className="mt-2">
          A rebuild can make a day&apos;s network activities again only from lines still kept, so a shorter time
          limits how far back a rebuild can redo them.
        </p>
      </Warning>
      <div className="md:w-1/2 md:pr-2">
        <NumberField
          label="Keep raw vendor data"
          unit="days"
          hint="30 to start; at least 1."
          min={1}
          shown={shown("raw_payload_retention_days")}
          status={statusOf("raw_payload_retention_days")}
          error={errors.raw_payload_retention_days}
          problem={rule({ unit: "days", min: 1 })}
          onSave={(days) => {
            change({ raw_payload_retention_days: days });
          }}
        />
      </div>
    </div>
  );
}

const ELSEWHERE: readonly { label: string; path: string; what: string }[] = [
  {
    label: "Mappings → Autotask settings",
    path: "/mappings/autotask",
    what: "the Autotask IDs a log uses (queue, statuses, priority, issue types, billing codes, note type and who can see the note), Writes to Autotask, and Logging to Autotask: the activity-log note, and whether Close ticket after logging starts ticked.",
  },
  {
    label: "Mappings → Email",
    path: "/mappings/email",
    what: "the minutes a sent email counts, and the public and Axon's own email domains.",
  },
  {
    label: "Mappings → Vendors",
    path: "/mappings/vendors",
    what: "vendors' email domains and phone numbers, whose email and calls aren't counted.",
  },
  {
    label: "Mappings → Network access",
    path: "/mappings/network",
    what: "the management ports and each one's service, the gap between connections, and the excluded sources.",
  },
];

/** A section with its own heading. */
function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <Panel aria-labelledby={id}>
      <h2 id={id} className="text-lg font-semibold tracking-tight">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </Panel>
  );
}

export function SettingsPage() {
  const { api } = useSession();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    retry: retryUnlessRefused,
  });

  function body(form: (settings: AppSettings) => ReactNode) {
    // A failed reload keeps the form it already has.
    if (settings.error && !settings.data) {
      return (
        <p role="alert" className="text-muted">
          The settings could not be loaded. Reload the page to try again.
        </p>
      );
    }
    if (!settings.data) {
      return (
        <p role="status" className="text-muted">
          Loading…
        </p>
      );
    }
    return form(settings.data);
  }

  return (
    <>
      <Panel>
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-muted">
          How work is counted and logged, the workdays, and how long raw data is kept. Each
          change saves as soon as you make it, and is in the audit log with the value before and after. Only owners
          can see this page.
        </p>
      </Panel>
      <Section id="counting-heading" title="Blocks and counting">
        {body((data) => (
          <BlocksAndCounting settings={data} />
        ))}
      </Section>
      <WorkdaysSettings />
      <Section id="data-heading" title="Data kept">
        {body((data) => (
          <DataKept settings={data} />
        ))}
      </Section>
      <Section id="elsewhere-heading" title="Other settings">
        <p className="text-muted">These stay where they&apos;re used, on Mappings:</p>
        <ul className="mt-2 flex flex-col gap-2">
          {ELSEWHERE.map((place) => (
            <li key={place.path}>
              <Link to={place.path} className={linkClass}>
                {place.label}
              </Link>
              : {place.what}
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
